import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useUser } from "@/lib/user-context";
import { useToast } from "@/hooks/use-toast";
import { PASSWORD_RULES } from "@shared/password-validation";
import { Lock, Check, X } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

interface ChangePasswordDialogProps {
  /** Non-closable forced mode (password expired or first login) */
  forced?: boolean;
  /** Controlled open state for voluntary mode */
  open?: boolean;
  /** Controlled open change handler for voluntary mode */
  onOpenChange?: (open: boolean) => void;
}

export function ChangePasswordDialog({ forced, open, onOpenChange }: ChangePasswordDialogProps) {
  const { user } = useUser();
  const { toast } = useToast();
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");

  const isOpen = forced ? !!user?.forcePasswordChange : open;

  const resetForm = () => {
    setCurrentPassword("");
    setNewPassword("");
    setConfirmPassword("");
    setError("");
  };

  const changePassword = useMutation({
    mutationFn: async () => {
      await apiRequest("PATCH", "/api/auth/change-password", {
        currentPassword,
        newPassword,
      });
    },
    onSuccess: () => {
      toast({ title: "Password changed successfully" });
      queryClient.invalidateQueries({ queryKey: ["/api/auth/me"] });
      resetForm();
      if (!forced && onOpenChange) {
        onOpenChange(false);
      }
    },
    onError: (err: Error) => {
      setError(err.message || "Failed to change password");
    },
  });

  const allRulesPass = PASSWORD_RULES.every((r) => r.test(newPassword));
  const passwordsMatch = newPassword === confirmPassword && confirmPassword.length > 0;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    if (!allRulesPass) {
      setError("Password does not meet complexity requirements");
      return;
    }
    if (!passwordsMatch) {
      setError("Passwords do not match");
      return;
    }
    changePassword.mutate();
  };

  const handleOpenChange = (val: boolean) => {
    if (forced) return; // Can't close forced dialog
    resetForm();
    onOpenChange?.(val);
  };

  if (!isOpen) return null;

  return (
    <Dialog open={true} onOpenChange={handleOpenChange}>
      <DialogContent
        className="sm:max-w-md"
        onPointerDownOutside={(e) => forced && e.preventDefault()}
        onEscapeKeyDown={(e) => forced && e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Lock className="h-5 w-5 text-primary" />
            Change Your Password
          </DialogTitle>
          <DialogDescription>
            {forced
              ? "For security, please change your password before continuing."
              : "Enter your current password and choose a new one."}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="cp-current">Current Password</Label>
            <Input
              id="cp-current"
              type="password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              required
              autoComplete="current-password"
              data-testid="input-current-password"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="cp-new">New Password</Label>
            <Input
              id="cp-new"
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              required
              autoComplete="new-password"
              data-testid="input-new-password"
            />
            {/* Real-time complexity checklist */}
            <div className="space-y-1 pt-1">
              {PASSWORD_RULES.map((rule) => {
                const passes = rule.test(newPassword);
                return (
                  <div key={rule.id} className="flex items-center gap-2 text-xs">
                    {passes ? (
                      <Check className="h-3.5 w-3.5 text-green-600 shrink-0" />
                    ) : (
                      <X className="h-3.5 w-3.5 text-muted-foreground/40 shrink-0" />
                    )}
                    <span className={passes ? "text-green-600" : "text-muted-foreground"}>
                      {rule.label}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="cp-confirm">Confirm New Password</Label>
            <Input
              id="cp-confirm"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              autoComplete="new-password"
              data-testid="input-confirm-password"
            />
            {confirmPassword.length > 0 && !passwordsMatch && (
              <p className="text-xs text-destructive">Passwords do not match</p>
            )}
          </div>
          {error && (
            <p className="text-sm text-destructive" data-testid="text-password-error">{error}</p>
          )}
          <DialogFooter>
            {!forced && (
              <Button type="button" variant="outline" onClick={() => handleOpenChange(false)}>
                Cancel
              </Button>
            )}
            <Button
              type="submit"
              disabled={changePassword.isPending || !allRulesPass || !passwordsMatch}
              data-testid="button-change-password"
            >
              {changePassword.isPending ? "Changing..." : "Change Password"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
